import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { UserIcon } from '@panwds/icons'
import { Button, List, ListItem, CardBody, CardHeader, Card, useOnOutsideClick } from '@panwds/react-ui'
import { logout } from '../../framework/actions'
import { _T } from '../../i18n'
import './MspActions.scss'
import type { VFC, MouseEventHandler, MouseEvent } from 'react'
import type { OidcState, Vars } from '../../framework/stateTypes'

const preventDefault = (e: MouseEvent<HTMLAnchorElement>) => {
  e.stopPropagation()
  e.preventDefault()
}

const FeedbackComp: VFC<{ isSideNavCollapsed: boolean, className?: string }> = ({ isSideNavCollapsed, className }) => (
  <button
    id='app-bar-vertical-feedback'
    aria-label='Give Feedback'
    className={classNames('app-bar-vertical-feedback', className)}
  >
    {isSideNavCollapsed ? '' : _T('Give Feedback')}
  </button>
)
export const Feedback = memo(FeedbackComp)

const AnnouncementsComp: VFC<{ isSideNavCollapsed: boolean }> = ({ isSideNavCollapsed }) => ( // TODO: remove nav-link
  <a href='#announcements' className={classNames('pendo-announcements', 'nav-link', isSideNavCollapsed && 'collapsed')} onClick={preventDefault}>
    <span className='announcements'>
      <span className='announcements-icon' />
      {isSideNavCollapsed ? <p className='sparky-left-nav-tooltip'>Announcements</p> : _T('Announcements')}
    </span>
  </a>
)

export const Announcements = memo(AnnouncementsComp)

function useShouldRender(isOpen: boolean): boolean {
  const [shouldRenderChildren, setShouldRenderChildren] = useState<boolean>(isOpen)

  useEffect(() => {
    let timeout: number

    if (isOpen) {
      setShouldRenderChildren(true)
    }
    else {
      timeout = window.setTimeout(() => {
        setShouldRenderChildren(false)
      }, 200)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [isOpen])

  return isOpen || shouldRenderChildren
}

const UserDropdownComp: VFC<{
  isSideNavCollapsed: boolean,
  userName?: string,
  className?: string,
  logout?: Function,
}> = ({ isSideNavCollapsed, userName = '', className, logout }) => {
  const [isOpen, setDropdownState] = useState(false)
  const isOpenRef = useRef(isOpen)
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const shouldRender = useShouldRender(isOpen)
  const opacity0 = !shouldRender || !isOpen

  useOnOutsideClick(useMemo(() => [btnRef, menuRef], []), () => {
    if (isOpenRef.current) {
      isOpenRef.current = false
      setDropdownState(false)
    }
  })

  const handleDropdownToggle = useCallback(() => {
    const open = !isOpenRef.current
    isOpenRef.current = open
    setDropdownState(open)
  }, [])

  if (!userName && !logout) {
    return null
  }

  const toggle = <Button
    ref={btnRef}
    aria-label={userName}
    appearance='tertiary-clear'
    size='lg'
    fullWidth
    dataTestId='sparky-user-toggle'
    addClassName={classNames('sparky-user-toggle-btn spf-text-[14px] spf-pl-4', isOpen && 'sparky-user-toggle-open')}
    icon={<UserIcon className='app-bar-user-icon' size='md' />}
    onClick={handleDropdownToggle}
  >
    {isSideNavCollapsed ? '' : userName}
  </Button>

  const menu = shouldRender && <Card
    addClassName={classNames('sparky-user-card spf-rounded', opacity0 && 'spf-opacity-0')}
    dataTestId='sparky-user-card'
  >
    <div ref={menuRef}>
      <CardHeader title={userName} addClassName='spf-h-14' />
      <CardBody addClassName='sparky-user-menu-body'>
        <List
          dataTestId='sparky-user-logout'
          onClick={logout as MouseEventHandler<any>}
          style={{ border: 'none' }}
        >
          <ListItem
            addClassName='sparky-user-logout spf-text-base spf-text-left'
          >{_T('Sign Out')}</ListItem>
        </List>
      </CardBody>
    </div>
  </Card>

  return <>
    {toggle}
    {menu}
  </>
}

export const UserDropdown = memo(UserDropdownComp)

interface MspActionsProps {
  hasPendo: boolean,
  isPrivateSite: boolean,
  oidc: OidcState,
  vars: Vars,
}
const mspActions = ({ hasPendo, isPrivateSite, oidc, vars }: MspActionsProps) => [
  oidc && {
    key: 'user',
    navComponent: UserDropdown,
    bypassTsgAvail: true,
    bypassGlobalAvail: true,
    mapStateToProps: ({ oidc: { oidcUser } = oidc }) => {
      if (!oidcUser?.profile) {
        return { userName: '', logout }
      }
      const {
        profile: {
          given_name: firstname = '',
          family_name: lastname = '',
          email = ''
        }
      } = oidcUser
      const userName = `${firstname} ${lastname}`.trim() || email
      return {
        userName,
        logout,
      }
    },
  },
  vars.feedback && hasPendo && !isPrivateSite && { // hide it for nov release
    key: 'feedback',
    title: _T('Give Feedback'),
    bypassTsgAvail: true,
    bypassGlobalAvail: true,
    navComponent: Feedback,
  },
].filter(Boolean)

export default mspActions
